import React, { useState, useEffect } from 'react';
import Modal from '../../common/Modal';
import ConfirmDialog from '../../common/ConfirmDialog';
import IssueComments from './IssueComments';
import IssueAttachments from './IssueAttachments';
import devIssueService from '../../../services/devIssueService';
import { Trash2, Save } from 'lucide-react';

const TYPE_BADGE = {
  bug: 'bg-red-100 text-red-700',
  feature: 'bg-purple-100 text-purple-700',
  improvement: 'bg-blue-100 text-blue-700',
};

const STATUS_OPTIONS = [
  { value: 'backlog', label: 'Backlog' },
  { value: 'up_next', label: 'Up Next' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'done', label: 'Done' },
  { value: 'rejected', label: 'Rejected' },
];

const IssueDetailModal = ({ isOpen, issue, onClose, onUpdate, onDelete }) => {
  const [form, setForm] = useState({});
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null, variant: 'warning', confirmText: 'Confirm' });

  useEffect(() => {
    if (isOpen && issue) {
      setForm({
        title: issue.title || '',
        description: issue.description || '',
        issue_type: issue.issue_type || 'bug',
        priority: issue.priority || 'medium',
        status: issue.status || 'backlog',
        reported_by: issue.reported_by || '',
        assignee_id: issue.assignee_id || '',
        story_points: issue.story_points != null ? String(issue.story_points) : '',
      });
      setComments(issue.comments || []);
      setAttachments(issue.attachments || []);
      setDirty(false);

      devIssueService.getAssignableUsers()
        .then(res => setUsers(res.data || []))
        .catch(() => setUsers([]));
    }
  }, [isOpen, issue]);

  const handleChange = (key, value) => {
    setForm(f => ({ ...f, [key]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description || null,
        issue_type: form.issue_type,
        priority: form.priority,
        status: form.status,
        reported_by: form.reported_by || null,
        assignee_id: form.assignee_id || null,
        story_points: form.story_points !== '' ? parseInt(form.story_points, 10) : null,
      };
      const res = await devIssueService.update(issue.id, payload);
      onUpdate({ ...res.data, comments, attachments });
      setDirty(false);
    } catch {
      // parent handles errors
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Issue',
      message: 'Delete this issue? This cannot be undone.',
      variant: 'danger',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await devIssueService.delete(issue.id);
          onDelete(issue.id);
          onClose();
        } catch {
          // parent handles errors
        }
      },
    });
  };

  const inputClass = "w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent";
  const selectClass = inputClass + " bg-white";

  if (!issue) return null;

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="max-w-3xl" showCloseButton={true}
      title={
        <div className="flex items-center gap-2">
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${TYPE_BADGE[form.issue_type] || 'bg-gray-100 text-gray-600'}`}>
            {form.issue_type ? form.issue_type.charAt(0).toUpperCase() + form.issue_type.slice(1) : ''}
          </span>
          <span className="text-lg font-semibold text-gray-800">Issue Detail</span>
        </div>
      }
    >
      <div className="flex gap-6">
        {/* Left — editable fields */}
        <div className="flex-1 space-y-4 min-w-0">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>
            <input
              value={form.title || ''}
              onChange={e => handleChange('title', e.target.value)}
              className={inputClass + " text-base font-medium"}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
            <textarea
              value={form.description || ''}
              onChange={e => handleChange('description', e.target.value)}
              rows={5}
              placeholder="No description yet…"
              className={inputClass + " resize-y"}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
              <select value={form.status || 'backlog'} onChange={e => handleChange('status', e.target.value)} className={selectClass}>
                {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
              <select value={form.issue_type || 'bug'} onChange={e => handleChange('issue_type', e.target.value)} className={selectClass}>
                <option value="bug">Bug</option>
                <option value="feature">Feature</option>
                <option value="improvement">Improvement</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Priority</label>
              <select value={form.priority || 'medium'} onChange={e => handleChange('priority', e.target.value)} className={selectClass}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Story Points</label>
              <input
                type="number" min="0" max="100"
                value={form.story_points || ''}
                onChange={e => handleChange('story_points', e.target.value)}
                placeholder="—"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Assignee</label>
              <select value={form.assignee_id || ''} onChange={e => handleChange('assignee_id', e.target.value)} className={selectClass}>
                <option value="">Unassigned</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Reported By</label>
              <input
                value={form.reported_by || ''}
                onChange={e => handleChange('reported_by', e.target.value)}
                placeholder="Name or email"
                className={inputClass}
              />
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <button
              onClick={handleDelete}
              className="flex items-center gap-1.5 text-sm text-red-500 hover:text-red-700 transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
            <button
              onClick={handleSave}
              disabled={!dirty || saving || !form.title?.trim()}
              className="flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-yellow-500 rounded-lg hover:bg-yellow-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-medium"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>

        {/* Right — comments + attachments */}
        <div className="w-72 flex-shrink-0 space-y-6 border-l border-gray-100 pl-6">
          <IssueAttachments
            issueId={issue.id}
            attachments={attachments}
            onAttachmentsChange={setAttachments}
          />
          <div className="border-t border-gray-100 pt-5">
            <IssueComments
              issueId={issue.id}
              comments={comments}
              onCommentsChange={setComments}
            />
          </div>
        </div>
      </div>
    </Modal>
    <ConfirmDialog
      isOpen={confirmDialog.isOpen}
      onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
      onConfirm={confirmDialog.onConfirm}
      title={confirmDialog.title}
      message={confirmDialog.message}
      variant={confirmDialog.variant}
      confirmText={confirmDialog.confirmText}
    />
    </>
  );
};

export default IssueDetailModal;
